// AdminInactiveUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import useStore from "../../../../useStore";
import Button from "../../../Elements/Button/Button";
import Loader from "../../../Elements/Loader/Loader";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import "./adminInactiveUsers.css";
import { buildMapsUrl } from "../../../../Utilities/Maps/BuildUri";
import useGeolocation from "../../../../Hooks/useGeolocation";
import { annotateWithDistance, filterByRadius, isValidCoord, RADIUS_KM_ADMINISTRADORES } from "../../../../Utilities/geocode";

const AdminInactiveUsers = () => {
    const showToast = useToast();
    const inactiveUsers = useStore((s) => s.inactiveUsers);
    const inactiveUsersCount = useStore((s) => s.inactiveUsersCount);
    const loadingInactiveUsers = useStore((s) => s.loadingInactiveUsers);
    const errorInactiveUsers = useStore((s) => s.errorInactiveUsers);
    const fetchDataInactiveUsers = useStore((s) => s.fetchDataInactiveUsers);
    const [q, setQ] = useState("");
    const activateUser = useStore(s => s.activateUser); // añade esto en tu store
    const [actLoading, setActLoading] = useState(null);
    const { status: geoStatus, coords } = useGeolocation();

    const onActivate = async (u) => {
        const id = u?.userId;
        if (!id) {
            showToast("error", "Este registro no tiene userId.");
            return;
        }
        try {
            setActLoading(id);
            const res = await activateUser(id);   // 👈 mandamos exactamente el id del backend
            if (res?.statusCode === 200) {
                showToast("success", "Usuario activado ✅");
                // no recargamos: el store ya lo remueve de la lista
            } else {
                showToast("error", res?.message || "No se pudo activar");
            }
        } finally {
            setActLoading(null);
        }
    };

    // usuario/rol (solo para mostrar en header)
    const user = (() => {
        try {
            const raw = localStorage.getItem("logindata");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    })();
    const role = user?.data?.role || user?.role || null;
    const isAdmin = role === "administrador";

    useEffect(() => {
        if (isAdmin) fetchDataInactiveUsers?.();
    }, [fetchDataInactiveUsers, isAdmin]);

    useEffect(() => {
        if (errorInactiveUsers) showToast("error", errorInactiveUsers);
    }, [errorInactiveUsers, showToast]);

    const mapsUrlFor = (u) => {
        return buildMapsUrl({
            lat: u?.delivery?.lat,
            lng: u?.delivery?.lng,
            direccion: u?.delivery?.direccion,
            name: u?.name,
        });
    };

    const rows = useMemo(() => {
        const base = Array.isArray(inactiveUsers) ? inactiveUsers : [];
        let candidates = base;

        // Si el admin concedió ubicación, filtra usuarios por radio (10 km)
        if (geoStatus === "granted" && coords && isValidCoord(coords.lat, coords.lng)) {
            const geoCandidates = base
                .map((u) => ({ ...u, lat: u?.delivery?.lat, lng: u?.delivery?.lng }))
                .filter((u) => isValidCoord(u.lat, u.lng));
            const withDist = annotateWithDistance({ lat: coords.lat, lng: coords.lng }, geoCandidates);
            candidates = filterByRadius(withDist, RADIUS_KM_ADMINISTRADORES);
        }

        // Orden: más reciente primero (updatedAt -> createdAt)
        const sorted = [...candidates].sort((a, b) => {
            const da = String(a.updatedAt || a.createdAt || "");
            const db = String(b.updatedAt || b.createdAt || "");
            return db.localeCompare(da);
        });

        // Búsqueda local
        const term = q.trim().toLowerCase();
        if (!term) return sorted;
        return sorted.filter((u) => {
            const name = (u.name || "").toLowerCase();
            const email = (u.email || "").toLowerCase();
            return name.includes(term) || email.includes(term);
        });
    }, [inactiveUsers, q, geoStatus, coords]);

    const hasData = rows.length > 0;

    return (
        <section className="container-fluid container-mh py-4 admin-inactive-users">
            {/* Header */}
            <div className="row mb-3">
                <div className="col-12">
                    {user ? (
                        <div className="welcome">
                            <h5 className="mb-1">
                                Bienvenido, <strong>{user.data?.name || "usuario"}</strong> 👋
                            </h5>
                            <small className="text-muted">Rol: {role || "desconocido"}</small>
                        </div>
                    ) : (
                        <h5>No has iniciado sesión</h5>
                    )}
                </div>
            </div>

            {/* Warning si no es admin */}
            {!isAdmin && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-warning">
                            No tienes permisos para ver usuarios inactivos (rol actual: {role || "desconocido"}).
                        </div>
                    </div>
                </div>
            )}

            {isAdmin && (
                <>
                    {/* Barra de acciones */}
                    <div className="row g-2 align-items-end mb-3">
                        <div className="col-12 col-md">
                            <h4 className="mb-1 page-title">Usuarios inactivos</h4>
                            <small className="text-muted">
                                Total: {inactiveUsersCount ?? (inactiveUsers?.length || 0)}
                            </small>
                            {geoStatus === "granted" && (
                                <small className="text-muted ms-2">Filtrando por ≤ {RADIUS_KM_ADMINISTRADORES} km</small>
                            )}
                        </div>

                        <div className="col-12 col-sm-6 col-md-5 col-lg-4">
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="form-control search-input"
                                placeholder="Buscar por nombre o correo…"
                            />
                        </div>

                        <div className="col-12 col-sm-auto">
                            <Button
                                text="Recargar"
                                color="blue"
                                action={() => fetchDataInactiveUsers?.()}
                                disabled={loadingInactiveUsers}
                                fill
                            />
                        </div>
                    </div>

                    {/* Loading inicial */}
                    {loadingInactiveUsers && !hasData && <Loader />}

                    {/* Empty */}
                    {!loadingInactiveUsers && !hasData && !errorInactiveUsers && (
                        <div className="row">
                            <div className="col-12">
                                <div className="alert alert-info">No hay usuarios inactivos por ahora.</div>
                            </div>
                        </div>
                    )}

                    {/* Tabla */}
                    {hasData && (
                        <div className="row">
                            <div className="col-12">
                                <div className="table-responsive shadow-sm rounded-3">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID usuario</th> {/* 👈 NUEVO */}
                                                <th>Nombre</th>
                                                <th>Correo</th>
                                                <th>Teléfono</th>
                                                <th>Rol</th>
                                                <th>Motivo</th>
                                                <th>Dirección</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((u) => {
                                                const url = mapsUrlFor(u);
                                                const id = u?.userId; // 👈 tomamos el id tal cual viene del backend
                                                return (
                                                    <tr key={id || `${u.email}-fallback`}> {/* usa userId si existe */}
                                                        <td>
                                                            <code className="small">{id || "-"}</code> {/* 👈 visible en la tabla */}
                                                        </td>
                                                        <td>{u.name || "-"}</td>
                                                        <td>{u.email || "-"}</td>
                                                        <td>{u.phone || "-"}</td>
                                                        <td className="text-capitalize">{u.role}</td>
                                                        <td>
                                                            {u.deactivatedReason === "reincidencia_no_show" ? (
                                                                <span className="badge bg-danger">
                                                                    Reincidente no-show
                                                                    {u.noShowDebt > 0 ? ` ($${u.noShowDebt} deuda)` : ""}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">En revisión</span>
                                                            )}
                                                        </td>
                                                        <td style={{ maxWidth: 360 }}>
                                                            {url ? (
                                                                <a
                                                                    className="maps-link"
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={u.delivery?.direccion || "Abrir en Google Maps"}
                                                                >
                                                                    Abrir en Google Maps
                                                                </a>
                                                            ) : (
                                                                <small className="text-muted">-</small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                text={actLoading === id ? "Activando..." : "Activar usuario"}
                                                                color="green"
                                                                fill
                                                                action={() => onActivate(u)}
                                                                disabled={actLoading === id || u.isActive === true || !id}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default AdminInactiveUsers;